import { PaymentElement, useElements } from "@stripe/react-stripe-js";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "@/atoms/alert";
import { Button } from "@/atoms/button";
import { CardFieldsBand } from "@/components/card-fields-skeleton";
import { Checkbox } from "@/atoms/checkbox";
import { CheckoutWallet } from "@/components/checkout-wallet";
import { StripeLine } from "@/components/stripe-line";
import { useOnline } from "@/components/use-online";
import { CheckoutTotals } from "./checkout-totals";
import { BillingFields } from "./billing-fields";
import { CouponField } from "./coupon-field";
import { useCheckoutPayment } from "./use-checkout-payment";
import { formatRunningMoney, fromCents } from "@/utils/money";
import { EMPTY_ADDRESS, toApiAddress } from "./billing-fields";
import type { Address } from "./billing-fields";
import type { AppliedPromo } from "./coupon-field";
import type { PaymentOutcome } from "./use-checkout-payment";
import type {
  PublicCheckoutDetails,
  PublicCheckoutProduct,
  PublicCheckoutProductPrice,
} from "@/data/checkout";

/**
 * Everything a person fills in and presses (checkout 1.4 regions `platformPay`, `form`,
 * `totals`, `consent`, `action`).
 *
 * The order is the template's and it is deliberate: the wallet first, because most people never
 * touch a field; then the card; then one total; then one consent sentence; then one orange
 * button carrying the amount. Nothing here is two columns, the card errors stay under the field
 * Stripe drew them for, and the only alert is the one that says the payment did not start.
 *
 * The button is disabled until the email is valid, the box is ticked and Stripe reports the card
 * complete, and a caption under it says which of those is missing, because a disabled button
 * with no reason is a dead end. Disabled keeps the ink label and stays in the tab order.
 */

const GYMKEE_TERMS = "https://gymkee.com/terms";

export interface PaymentSheetProps {
  checkout: PublicCheckoutDetails;
  products: PublicCheckoutProduct[];
  priceFor: (product: PublicCheckoutProduct) => PublicCheckoutProductPrice;
  mainPrice: PublicCheckoutProductPrice;
  isSubscription: boolean;
  currency: string;
  /** The total the page is showing, in cents. */
  totalCents: number;
  promo: AppliedPromo | null;
  onPromoChange: (promo: AppliedPromo | null) => void;
  initialPromoCode: string;
  coachName: string;
  onDone: (outcome: PaymentOutcome) => void;
}

/** A shape check, not a deliverability check: the field says what is wrong, not whether it exists. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function PaymentSheet({
  checkout,
  products,
  priceFor,
  mainPrice,
  isSubscription,
  currency,
  totalCents,
  promo,
  onPromoChange,
  initialPromoCode,
  coachName,
  onDone,
}: PaymentSheetProps) {
  const { t } = useTranslation();
  const elements = useElements();
  const online = useOnline();

  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState<Address>(EMPTY_ADDRESS);
  const [consent, setConsent] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardMounted, setCardMounted] = useState(false);
  const [walletAvailable, setWalletAvailable] = useState<boolean | null>(null);

  const { collectBillingAddress, collectPhoneNumber, allowPromotionCodes } =
    checkout.options;
  const freeCheckout = totalCents === 0;

  const { pay, paying, error, ready } = useCheckoutPayment({
    paymentLinkId: checkout.paymentLinkId,
    currency,
    shownTotalCents: totalCents,
    onDone,
  });

  /* The total moved (a code was applied or removed): Stripe is told, rather than the whole
     Elements tree being remounted, which would throw away a card already typed. */
  const lastAmount = useRef(totalCents);
  useEffect(() => {
    if (!elements || lastAmount.current === totalCents || freeCheckout) return;
    lastAmount.current = totalCents;
    elements.update({ amount: Math.max(1, totalCents) });
  }, [elements, totalCents, freeCheckout]);

  const emailValid = EMAIL_SHAPE.test(email.trim());
  const cardReady = freeCheckout || cardComplete;
  const canPay = emailValid && consent && cardReady && ready && !paying;
  const emailError =
    emailTouched && !emailValid ? t("gymkeePay.checkout.emailInvalid") : null;

  const submit = () => {
    if (!canPay) return;
    void pay({
      email: email.trim(),
      name: name.trim() || undefined,
      phone: collectPhoneNumber ? phone.trim() || undefined : undefined,
      address: collectBillingAddress ? toApiAddress(address) : undefined,
      promotionCode: promo?.code,
    });
  };

  return (
    <form
      className="ds-pay-form gap-gk-6 flex flex-col"
      aria-busy={paying || undefined}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {/* The whole form goes inert while the processor answers, in one place, so no control can
          be pressed twice and no value can change under a payment in flight. */}
      <fieldset
        disabled={paying}
        className="gap-gk-6 flex min-w-0 flex-col border-0 p-0"
      >
        <CheckoutWallet
          isSubscription={isSubscription}
          collectPhoneNumber={collectPhoneNumber}
          collectBillingAddress={collectBillingAddress}
          available={walletAvailable}
          onAvailable={setWalletAvailable}
          onConfirm={(billing, failed) => {
            void pay({
              email: email.trim(),
              name: name.trim() || undefined,
              phone: phone.trim() || undefined,
              address: collectBillingAddress
                ? toApiAddress(address)
                : undefined,
              promotionCode: promo?.code,
              walletBillingDetails: billing,
            }).then((paid) => {
              if (!paid) failed();
            });
          }}
          hidden={freeCheckout}
        />

        <div className="gap-gk-4 flex flex-col">
          <BillingFields
            values={{ email, name, phone, address }}
            emailError={emailError}
            collectPhoneNumber={collectPhoneNumber}
            collectBillingAddress={collectBillingAddress}
            onEmail={setEmail}
            onEmailBlur={() => setEmailTouched(true)}
            onName={setName}
            onPhone={setPhone}
            onAddress={setAddress}
          />

          {/* The card fields, and the band they hold open until they are really drawn. */}
          {!freeCheckout && (
            <CardFieldsBand ready={cardMounted}>
              <PaymentElement
                options={{
                  layout: { type: "tabs" },
                  wallets: {
                    applePay: "never",
                    googlePay: "never",
                    link: "never",
                  },
                  /* A field marked "never" is a promise to hand Stripe that value at confirm
                     ("If you disable collecting certain fields with the fields option, you must
                     pass that same data to stripe.confirmPayment or we'll reject the payment").
                     Email and name are always ours to pass; phone and address only when the link
                     collects them, so they stay Stripe's when it does not. */
                  fields: {
                    billingDetails: {
                      email: "never",
                      name: "never",
                      phone: collectPhoneNumber ? "never" : "auto",
                      address: collectBillingAddress ? "never" : "auto",
                    },
                  },
                  terms: { card: "never" },
                }}
                onChange={(event) => setCardComplete(event.complete)}
                onReady={() => setCardMounted(true)}
              />
            </CardFieldsBand>
          )}

          {(allowPromotionCodes || !!initialPromoCode) && (
            <CouponField
              paymentLinkId={checkout.paymentLinkId}
              currency={currency}
              applied={promo}
              onApplied={onPromoChange}
              initialCode={initialPromoCode}
            />
          )}
        </div>

        <CheckoutTotals
          products={products}
          priceFor={priceFor}
          mainPrice={mainPrice}
          isSubscription={isSubscription}
          currency={currency}
          totalCents={totalCents}
          promo={promo}
          coachName={coachName}
        />

        <Checkbox
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          label={
            <Trans
              i18nKey={
                isSubscription
                  ? "gymkeePay.checkout.consentSubscription"
                  : "gymkeePay.checkout.consentOneOff"
              }
              values={{ coach: coachName }}
              components={[
                <span key="0" />,
                <a
                  key="1"
                  href={GYMKEE_TERMS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  style={{ color: "var(--gk-text-orange-ink)" }}
                />,
              ]}
            />
          }
        />

        <div className="gap-gk-3 flex flex-col">
          {error && <Alert tone="danger">{error}</Alert>}
          {/* data.offline: a payment that cannot leave is not offered and then failed. */}
          {online && (
            <>
              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                loading={paying}
                disabled={!canPay}
              >
                {t("gymkeePay.checkout.payAmount", {
                  amount: formatRunningMoney(fromCents(totalCents), currency),
                })}
              </Button>
              {!canPay && !paying && (
                <p className="text-center text-[13px] text-[var(--gk-text-tertiary)]">
                  {t("gymkeePay.checkout.payHint")}
                </p>
              )}
            </>
          )}
          <StripeLine />
        </div>
      </fieldset>
    </form>
  );
}
